import React from 'react';

import type { BlocoCarregado } from '../hooks/useComprasDashboard';
import type { StatusCompras } from '../../../api/comprasDashboardApi';

/**
 * "Atualizado há X" — o carimbo do painel (at-100).
 *
 * 🔴 **Três estados, nunca dois** (regra que veio do carimbo dos Comissionamentos): a data do
 * último SUCESSO, "ainda não sincronizado" e "não foi possível verificar". Colapsar os dois
 * últimos faria a tela afirmar que nunca houve sincronização quando o que houve foi uma falha de
 * consulta — e a pessoa iria procurar um problema que não existe.
 *
 * ⚠️ Data do último **sucesso**, não da última tentativa: uma varredura que falhou não atualizou
 * nada, e datar por ela seria afirmação falsa sobre o frescor do dado.
 */
export function duracaoDesde(iso: string | null, agora = Date.now()): string | null {
  if (!iso) return null;
  const minutos = Math.floor((agora - new Date(iso).getTime()) / 60000);
  if (minutos < 1) return 'agora há pouco';
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  const dias = Math.floor(horas / 24);
  return `há ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}

interface Props {
  status: BlocoCarregado<StatusCompras>;
}

export default function CarimboSync({ status }: Props) {
  if (status.carregando) {
    return <span className="compras-carimbo compras-carimbo--neutro">Verificando atualização...</span>;
  }

  if (status.erro || !status.dados) {
    return (
      <span className="compras-carimbo compras-carimbo--atencao">
        Não foi possível verificar quando os dados foram atualizados
      </span>
    );
  }

  if (!status.dados.configurado) {
    return (
      <span className="compras-carimbo compras-carimbo--atencao">
        Integração com o Nasajon não configurada neste ambiente
      </span>
    );
  }

  const quente = status.dados.camadas.QUENTE;
  const pedidos = status.dados.camadas.PEDIDOS;
  const sincronizacao = status.dados.sincronizacao;

  if (quente.nuncaExecutada) {
    return <span className="compras-carimbo compras-carimbo--neutro">Ainda não sincronizado</span>;
  }

  const idade = duracaoDesde(quente.ultimoSucesso);
  const idadePedidos = duracaoDesde(pedidos.ultimoSucesso);

  /*
   * at-108 — pausado, o carimbo precisa dizer isso, e não só "Atualizado há 5 h".
   *
   * A idade sozinha é lida como defasagem temporária, quando na verdade o número não vai mudar até
   * alguém retomar. E a autoria vem junto porque é o que transforma o aviso em ação: quem lê sabe
   * a quem perguntar.
   */
  if (sincronizacao?.pausado) {
    const quando = sincronizacao.pausadoEm
      ? new Date(sincronizacao.pausadoEm).toLocaleString('pt-BR', {
          dateStyle: 'short',
          timeStyle: 'short',
        })
      : null;
    return (
      <span className="compras-carimbo compras-carimbo--neutro">
        Sincronização pausada
        {quando ? ` em ${quando}` : ''}
        {sincronizacao.pausadoPor ? ` por ${sincronizacao.pausadoPor.nome}` : ''}
        {idade ? ` · último dado ${idade}` : ''}
      </span>
    );
  }

  return (
    <span className="compras-carimbo">
      Atualizado {idade ?? 'em momento desconhecido'}
      {idadePedidos ? ` · pedidos ${idadePedidos}` : ' · pedidos ainda não sincronizados'}
      {/* No ritmo diário a idade alta é o esperado: dizer o ritmo evita que ela seja lida como atraso. */}
      {sincronizacao?.modo === 'DIARIO'
        ? ` · leitura única diária às ${sincronizacao.horaDaLeituraDiaria}h`
        : ''}
      {quente.statusDaUltimaTentativa === 'FALHOU' ? ' · a última tentativa falhou' : ''}
    </span>
  );
}
